import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useUnifiedApi } from '../../shared/useUnifiedApi';
import {
  ArrowLeft, Link2, Plus, Trash2, Clock, CheckCircle2, XCircle, Building2,
  Mail, Send, AlertTriangle, Users, Wrench, UserPlus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  active: { label: 'Connecte', color: 'bg-green-500/15 text-green-400', icon: CheckCircle2 },
  accepted: { label: 'Connecte', color: 'bg-green-500/15 text-green-400', icon: CheckCircle2 },
  pending: { label: 'En attente', color: 'bg-amber-500/15 text-amber-400', icon: Clock },
  rejected: { label: 'Refuse', color: 'bg-red-500/15 text-red-400', icon: XCircle },
};

const AdminConnectionsPage = ({ token: propToken, mode: propMode }) => {
  const adminAuth = useAdminAuth();
  const token = propToken || adminAuth.token;
  const isAtelier = propMode === 'atelier' || adminAuth.isAtelier;
  const isSuperAdmin = adminAuth.user?.role === 'super_admin';
  const hasAtelierModule = adminAuth.hasModule?.('module_entretiens');
  const canBeAtelier = isAtelier || (isSuperAdmin && hasAtelierModule);
  const mode = isAtelier ? 'atelier' : 'admin';
  const { get, post, del } = useUnifiedApi(token, mode);
  const navigate = useNavigate();

  const [connections, setConnections] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCompany, setInviteCompany] = useState('');
  const [inviteType, setInviteType] = useState(canBeAtelier ? 'atelier_client' : 'my_atelier');
  const [sending, setSending] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [connRes, invRes] = await Promise.all([
      get('/connections'),
      get('/connections/invitations'),
    ]);
    if (connRes.success) setConnections(connRes.data || []);
    if (invRes.success) setInvitations(invRes.data || []);
    setLoading(false);
  }, [get]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sendInvite = async () => {
    if (!inviteEmail.trim()) { toast.error('Courriel requis'); return; }
    setSending(true);
    const payload = {
      email: inviteEmail.trim(),
      company_name: inviteCompany.trim(),
    };
    // If admin invites a client for their atelier
    if (!isAtelier && inviteType === 'atelier_client') {
      payload.invite_as = 'atelier_client';
    }
    const result = await post('/connections/invite', payload);
    if (result.success) {
      toast.success(result.data.message || 'Invitation envoyee');
      setInviteEmail('');
      setInviteCompany('');
      setShowInvite(false);
      fetchData();
    } else {
      toast.error(result.error);
    }
    setSending(false);
  };

  const acceptInvitation = async (connId) => {
    const result = await post(`/connections/invitations/${connId}/accept`, {});
    if (result.success) {
      toast.success('Connexion etablie');
      fetchData();
    } else {
      toast.error(result.error);
    }
  };

  const rejectInvitation = async (connId) => {
    const result = await post(`/connections/invitations/${connId}/reject`, {});
    if (result.success) {
      toast.success('Invitation refusee');
      fetchData();
    } else {
      toast.error(result.error);
    }
  };

  const removeConnection = async (connId) => {
    if (!window.confirm('Retirer cette connexion?')) return;
    const result = await del(`/connections/${connId}`);
    if (result.success) {
      toast.success('Connexion retiree');
      fetchData();
    } else {
      toast.error(result.error);
    }
  };

  // Separate connections by type
  const myAteliers = connections.filter(c => c.connection_type === 'atelier');
  const myClients = connections.filter(c => c.connection_type === 'atelier_client');
  const myMechanics = connections.filter(c => c.connection_type === 'mechanic');
  // For pure atelier role, all connections are clients
  const atelierConnections = isAtelier ? connections : [];

  const totalActive = connections.filter(c => c.status === 'active' || c.status === 'accepted').length;
  const totalPending = connections.filter(c => c.status === 'pending').length;

  const backPath = '/admin/fleet';

  const renderConnectionCard = (conn, displayRole) => {
    const status = conn.status === 'accepted' ? 'active' : conn.status;
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const StatusIcon = cfg.icon;

    let displayName, displayEmail, typeLabel, typeColor;
    if (displayRole === 'client') {
      displayName = conn.admin_company || conn.admin_name || conn.admin_email || 'Client';
      displayEmail = conn.admin_email || '';
      typeLabel = 'Client';
      typeColor = 'bg-blue-500/20 text-blue-400';
    } else if (displayRole === 'atelier') {
      displayName = conn.atelier_name || 'Atelier';
      displayEmail = conn.atelier_email || '';
      typeLabel = 'Atelier';
      typeColor = 'bg-purple-500/20 text-purple-400';
    } else {
      displayName = conn.mechanic_name || conn.mechanic_email || 'Mecanicien';
      displayEmail = conn.mechanic_email || '';
      typeLabel = 'Mecanicien';
      typeColor = 'bg-teal-500/20 text-teal-400';
    }

    return (
      <div key={conn.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-testid={`connection-${conn.id}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center">
              {displayRole === 'client' ? <Building2 size={18} className="text-blue-400" /> :
               displayRole === 'atelier' ? <Wrench size={18} className="text-purple-400" /> :
               <Users size={18} className="text-teal-400" />}
            </div>
            <div>
              <p className="text-white font-medium text-sm">{displayName}</p>
              {displayEmail && <p className="text-slate-500 text-xs">{displayEmail}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-medium ${typeColor}`}>
              {typeLabel}
            </span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${cfg.color}`}>
              <StatusIcon size={10} /> {cfg.label}
            </span>
            <button
              onClick={() => removeConnection(conn.id)}
              className="text-slate-600 hover:text-red-400 transition-colors p-1"
              data-testid={`remove-${conn.id}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
        {conn.not_on_platform && conn.status === 'pending' && (
          <div className="mt-3 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
            <p className="text-[11px] text-amber-300">Pas encore sur ProPilote</p>
          </div>
        )}
        {conn.accepted_at && (
          <p className="text-[10px] text-slate-600 mt-2">Connecte depuis {conn.accepted_at?.split('T')[0]}</p>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="connections-page">
      {/* Header */}
      <button
        onClick={() => navigate(backPath)}
        className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors"
        data-testid="back-to-fleet-btn"
      >
        <ArrowLeft size={18} />
        <span>Retour a la flotte</span>
      </button>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="connections-title">
            <Users className="text-amber-500" />
            Connexions
          </h1>
          <p className="text-slate-400 text-sm">Gerez vos connexions ateliers, clients et mecaniciens</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-white">{totalActive}</p>
          <p className="text-xs text-green-400">Connectees</p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-white">{totalPending}</p>
          <p className="text-xs text-amber-400">En attente</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-white">{invitations.length}</p>
          <p className="text-xs text-blue-400">Invitations recues</p>
        </div>
      </div>

      {/* Invitations received */}
      {invitations.length > 0 && (
        <div className="space-y-3" data-testid="received-invitations">
          <p className="text-sm font-medium text-white flex items-center gap-2">
            <Mail size={14} className="text-amber-400" /> Invitations recues
          </p>
          {invitations.map(inv => (
            <div key={inv.id} className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid={`invitation-${inv.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium flex items-center gap-2">
                    <Building2 size={14} className="text-amber-400" />
                    {inv.atelier_name || inv.admin_company || 'Entreprise'}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Souhaite se connecter a votre flotte
                  </p>
                  {inv.invited_by_name && (
                    <p className="text-[10px] text-slate-500 mt-1">Invite par: {inv.invited_by_name}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => acceptInvitation(inv.id)}
                  className="flex-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                  data-testid={`accept-inv-${inv.id}`}
                >
                  <CheckCircle2 size={14} /> Accepter
                </button>
                <button
                  onClick={() => rejectInvitation(inv.id)}
                  className="flex-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                  data-testid={`reject-inv-${inv.id}`}
                >
                  <XCircle size={14} /> Refuser
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite button */}
      <Button
        onClick={() => setShowInvite(!showInvite)}
        className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold py-3"
        data-testid="invite-btn"
      >
        <UserPlus size={18} className="mr-2" />
        Nouvelle invitation
      </Button>

      {/* Invite form */}
      {showInvite && (
        <div className="bg-slate-900 border border-amber-500/30 rounded-xl p-4 space-y-3" data-testid="invite-form">
          <p className="text-sm text-white font-medium">Nouvelle invitation</p>

          {/* Invite type selector (only for admin with atelier capability) */}
          {!isAtelier && canBeAtelier && (
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">Type d'invitation</label>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => setInviteType('atelier_client')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    inviteType === 'atelier_client'
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                      : 'bg-slate-800 text-slate-400 border border-slate-700'
                  }`}
                  data-testid="invite-type-client"
                >
                  <Building2 size={14} /> Inviter un client
                </button>
                <button
                  onClick={() => setInviteType('my_atelier')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    inviteType === 'my_atelier'
                      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40'
                      : 'bg-slate-800 text-slate-400 border border-slate-700'
                  }`}
                  data-testid="invite-type-atelier"
                >
                  <Wrench size={14} /> Connecter un atelier
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider">
              {isAtelier ? "Courriel du client"
               : inviteType === 'atelier_client' ? "Courriel du client"
               : "Courriel de l'atelier / mecanicien"}
            </label>
            <div className="flex items-center gap-2 mt-1">
              <Mail size={16} className="text-slate-500 flex-shrink-0" />
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="courriel@entreprise.com"
                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500"
                data-testid="invite-email-input"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider">Nom de l'entreprise (optionnel)</label>
            <div className="flex items-center gap-2 mt-1">
              <Building2 size={16} className="text-slate-500 flex-shrink-0" />
              <input
                type="text"
                value={inviteCompany}
                onChange={e => setInviteCompany(e.target.value)}
                placeholder="Transport ABC inc."
                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500"
                data-testid="invite-company-input"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowInvite(false)}
              variant="outline"
              className="flex-1 border-slate-700 text-slate-400"
            >
              Annuler
            </Button>
            <Button
              onClick={sendInvite}
              disabled={sending}
              className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              data-testid="send-invite-btn"
            >
              {sending
                ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                : <><Send size={14} className="mr-1" /> Envoyer</>}
            </Button>
          </div>
          {inviteType === 'atelier_client' && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <p className="text-[11px] text-blue-300">
                Le client recevra une invitation a connecter sa flotte a votre atelier. Ses vehicules apparaitront dans votre page Atelier sous "Clients".
              </p>
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-3 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
        </div>
      ) : connections.length === 0 && invitations.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
          <Link2 size={36} className="mx-auto text-slate-700 mb-3" />
          <p className="text-slate-400 text-sm font-medium">Aucune connexion</p>
          <p className="text-slate-600 text-xs mt-1">Invitez des clients ou connectez-vous a un atelier</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Pure atelier mode: show all connections as clients */}
          {isAtelier && atelierConnections.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-blue-400 flex items-center gap-2">
                <Building2 size={14} /> Mes clients ({atelierConnections.length})
              </p>
              {atelierConnections.map(c => renderConnectionCard(c, 'client'))}
            </div>
          )}

          {/* Admin mode: My atelier clients */}
          {!isAtelier && myClients.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-blue-400 flex items-center gap-2">
                <Building2 size={14} /> Clients de mon atelier ({myClients.length})
              </p>
              {myClients.map(c => renderConnectionCard(c, 'client'))}
            </div>
          )}

          {/* Admin mode: My ateliers */}
          {!isAtelier && myAteliers.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-purple-400 flex items-center gap-2">
                <Wrench size={14} /> Mes ateliers ({myAteliers.length})
              </p>
              {myAteliers.map(c => renderConnectionCard(c, 'atelier'))}
            </div>
          )}

          {/* Admin mode: My mechanics */}
          {!isAtelier && myMechanics.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-teal-400 flex items-center gap-2">
                <Users size={14} /> Mes mecaniciens ({myMechanics.length})
              </p>
              {myMechanics.map(c => renderConnectionCard(c, 'mechanic'))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminConnectionsPage;
